import React, { useEffect } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import api from "../../api";
import { getCurrentCustomer, setAuth, getAuthToken } from "../../utils/auth";
import { useTranslation } from "react-i18next";

const UpdateProfile = () => {
  const { t } = useTranslation();

  const SignupSchema = Yup.object().shape({
    fullName: Yup.string().required(t("updateProfile.nameRequired", "Full Name is required!")),
    email: Yup.string()
      .email(t("updateProfile.emailInvalid", "Invalid email"))
      .required(t("updateProfile.emailRequired", "Email address is required!")),
    phoneNumber: Yup.string(),
    yourAddress: Yup.string().required(t("updateProfile.addressRequired", "Your Address is required!")),
  });

  const customer = getCurrentCustomer();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="max-w-screen-2xl">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h2 className="text-xl font-semibold mb-5 text-black">
              {t("updateProfile.title", "Update Profile")}
            </h2>
          </div>
        </div>
      </div>

      <Formik
        initialValues={{
          fileImage: "",
          fullName:
            [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") ||
            customer?.name ||
            "",
          yourAddress: customer?.address_line_1 || "",
          phoneNumber: customer?.phone || "",
          email: customer?.email || "",
        }}
        validationSchema={SignupSchema}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          setStatus(null);
          if (!customer?.id) {
            setStatus({ success: false, message: t("updateProfile.sessionError", "Customer session not found. Please login again.") });
            setSubmitting(false);
            return;
          }
          const nameParts = values.fullName.trim().split(/\s+/);
          const first_name = nameParts.shift() || values.fullName.trim();
          const last_name = nameParts.join(" ");
          try {
            const res = await api.put(`/customers/${customer.id}`, {
              first_name,
              last_name,
              email: values.email,
              phone: values.phoneNumber || null,
              address_line_1: values.yourAddress,
            });
            const updatedCustomer = res?.data?.data || {};
            setAuth({
              token: getAuthToken(),
              tokenType: "Bearer",
              customer: updatedCustomer,
            });
            setStatus({ success: true, message: t("updateProfile.updateSuccess", "Profile updated successfully.") });
          } catch (err) {
            setStatus({ success: false, message: err?.response?.data?.message || t("updateProfile.updateFail", "Failed to update profile.") });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, status, isSubmitting }) => {
          return (
            <Form>
              <div className=" mt-5 md:mt-0 md:col-span-2">
                <div className="bg-white space-y-6">
                  <div>
                    <label
                      htmlFor="fileImage"
                      className="block text-gray-500 font-medium text-sm leading-none mb-2"
                    >
                      {t("updateProfile.photo", "Photo")}
                    </label>
                    <div className="mt-1 flex items-center">
                      <div className="w-full text-center">
                        <label tabIndex="0" className="flex flex-col w-full h-32 px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer ">
                          <span className="mx-auto flex justify-center">
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-3xl text-emerald-500"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <polyline points="16 16 12 12 8 16"></polyline>
                              <line x1="12" y1="12" x2="12" y2="21"></line>
                              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
                              <polyline points="16 16 12 12 8 16"></polyline>
                            </svg>
                          </span>
                          <p className="text-sm mt-2 text-black">
                            {t("updateProfile.dragImage", "Drag your image here")}
                          </p>
                          <em className="text-xs text-gray-400">
                            {t("updateProfile.imageType", "(Only *.jpeg and *.png images will be accepted)")}
                          </em>

                          <Field
                            id="fileImage"
                            name="fileImage"
                            accept="image/*"
                            type="file"
                            tabIndex="-1"
                       autoComplete="off"
                            style={{ display: "none" }}
                          />
                        </label>

                        <aside className="flex flex-row flex-wrap mt-4">
                          <div className="inline-flex border rounded-md border-gray-100 w-24 max-h-24 p-2">
                            <img src="https://i.ibb.co/KXBBnPx/team-3.jpg" />
                          </div>
                        </aside>
                      </div>
                    </div>
                    <span className="text-red-400 text-sm mt-2">
                      {errors.firstName}
                    </span>
                  </div>
                </div>
                <div className="mt-10 sm:mt-0">
                  <div className="md:grid-cols-6 md:gap-6">
                    <div className="mt-5 md:mt-0 md:col-span-2">
                      <div className="lg:mt-6 mt-4 bg-white">
                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-3">
                            <label
                              htmlFor="fullName"
                              className="block text-gray-500 font-medium text-sm leading-none mb-2"
                            >
                              {t("updateProfile.fullName", "Full Name")}
                            </label>
                            <div className="relative">
                              <Field
                                id="fullName"
                                name="fullName"
                                placeholder={t("updateProfile.fullName", "Full Name")}
                                className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                              />
                            </div>
                            <span className="text-red-400 text-sm mt-2">
                              {errors.fullName && touched.fullName && (
                                <div>{errors.fullName}</div>
                              )}
                            </span>
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <label
                              htmlFor="yourAddress"
                              className="block text-gray-500 font-medium text-sm leading-none mb-2"
                            >
                              {t("updateProfile.yourAddress", "Your Address")}
                            </label>
                            <div className="relative">
                              <Field
                                id="yourAddress"
                                name="yourAddress"
                                placeholder={t("updateProfile.yourAddress", "Your Address")}
                                className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                              />
                            </div>
                            <span className="text-red-400 text-sm mt-2">
                              {errors.yourAddress && touched.yourAddress && (
                                <div>{errors.yourAddress}</div>
                              )}
                            </span>
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <label
                              htmlFor="phoneNumber"
                              className="block text-gray-500 font-medium text-sm leading-none mb-2"
                            >
                              {t("updateProfile.phoneMobile", "Phone/Mobile")}
                            </label>
                            <div className="relative">
                              <Field
                                id="phoneNumber"
                                name="phoneNumber"
                                placeholder={t("updateProfile.phoneMobile", "Phone/Mobile")}
                                className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                              />
                            </div>
                            <span className="text-red-400 text-sm mt-2">
                              {errors.phoneNumber && touched.phoneNumber && (
                                <div>{errors.phoneNumber}</div>
                              )}
                            </span>
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <label
                              htmlFor="email"
                              className="block text-gray-500 font-medium text-sm leading-none mb-2"
                            >
                              {t("updateProfile.emailAddress", "Email Address")}
                            </label>
                            <div className="relative">
                              <Field
                                id="email"
                                name="email"
                                placeholder={t("updateProfile.emailAddress", "Email Address")}
                                className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                              />
                            </div>
                            <span className="text-red-400 text-sm mt-2">
                              {errors.email && touched.email && (
                                <div>{errors.email}</div>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-6 sm:col-span-3 mt-5 text-right">
                          {status && status.message ? (
                            <p className={`text-sm mb-3 ${status.success ? "text-emerald-600" : "text-red-500"}`}>
                              {status.message}
                            </p>
                          ) : null}
                          <button
                            type="submit"
                            className="md:text-sm leading-5 inline-flex items-center cursor-pointer transition ease-in-out duration-300 font-medium text-center justify-center border-0 border-transparent rounded-md placeholder-white focus-visible:outline-none focus:outline-none bg-emerald-500 text-white px-5 md:px-6 lg:px-8 py-2 md:py-3 lg:py-3 hover:text-white hover:bg-emerald-600 h-12 mt-1 text-sm lg:text-sm w-full sm:w-auto"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? t("updateProfile.updatingButton", "Updating...") : t("updateProfile.updateButton", "Update Profile")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default UpdateProfile;
